/**
 * The camera module: what you look like, before anybody else can see it.
 *
 * It is dark in both themes, like the call surface and for the same reason — a
 * video frame is a light source, and light chrome around it puts a glare
 * around every face. Here it also does something else: it makes the lobby's
 * preview and the call's tiles read as the same object, so joining feels like
 * the picture moving rather than a new screen appearing.
 *
 * Used by the lobby (`routes/home.tsx`) and by the direct-link prejoin
 * (`features/call/prejoin.tsx`). One component, so the two cannot drift.
 */

import * as React from 'react'
import { AlertTriangle, Camera, CameraOff, Mic, MicOff, Volume2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { speakerColorVar } from '@/lib/format'
import { dedupeDevices, useDeviceInventory, type DeviceOption } from '../media'
import type { Preview } from '../use-preview'
import { CallKeyframes } from './call-styles'
import { MicMeter } from './mic-meter'
import { CallButton, MENU_SHEET, Menu, MenuChevron, MenuItem, MenuLabel } from './primitives'

export function MediaPreview({
  preview,
  displayName,
  /**
   * Which surface this is sitting on.
   *
   * The frame itself is dark either way; only the device pickers below it
   * change, because a row of white pills on the near-black prejoin screen
   * reads as chrome from a different application.
   */
  tone = 'light',
  compact = false,
  className,
}: {
  preview: Preview
  displayName: string
  tone?: 'light' | 'dark'
  /** Keep the device controls on one shrinking row on compact phone layouts. */
  compact?: boolean
  className?: string
}) {
  const inventory = useDeviceInventory(preview.revision)
  const video = React.useRef<HTMLVideoElement>(null)

  React.useEffect(() => {
    const element = video.current
    if (!element) return
    element.srcObject = preview.stream
    // Autoplay can still reject — a muted, inline video is normally allowed,
    // but a browser in a locked-down profile will refuse. Nothing to recover
    // from; the poster state below is what the person sees.
    void element.play().catch(() => undefined)
    return () => {
      element.srcObject = null
    }
  }, [preview.stream])

  const showVideo = preview.status === 'ready' && preview.hasCamera && preview.cameraOn

  return (
    <div className={cn('space-y-3', className)}>
      <CallKeyframes />

      <div className="relative aspect-video w-full overflow-hidden rounded-2xl border border-white/10 bg-[#0c0b0a] shadow-[0_20px_60px_-30px_rgb(0_0_0/0.9)]">
        <video
          ref={video}
          autoPlay
          muted
          playsInline
          // Mirrored, always. You reach right and your reflection reaches
          // right; an unmirrored self-view makes people feel clumsy and is the
          // single most common complaint about home-made call UIs. Only the
          // SELF view is mirrored — what is sent to everyone else is not.
          className={cn(
            'size-full scale-x-[-1] object-cover transition-opacity duration-300',
            showVideo ? 'opacity-100' : 'opacity-0',
          )}
        />

        {!showVideo ? <PreviewPoster preview={preview} displayName={displayName} /> : null}

        {/* Controls float over the picture rather than sitting under it, so the
            frame stays the largest thing on the card. */}
        <div className="absolute inset-x-0 bottom-0 flex items-center gap-2 bg-gradient-to-t from-black/70 via-black/25 to-transparent p-3">
          <CallButton
            tone={preview.micOn && preview.hasMic ? 'on' : 'off'}
            label={preview.micOn ? 'Mute microphone' : 'Unmute microphone'}
            disabled={!preview.hasMic}
            onClick={() => preview.setMicOn(!preview.micOn)}
          >
            {preview.micOn && preview.hasMic ? <Mic /> : <MicOff />}
          </CallButton>

          <CallButton
            tone={preview.cameraOn && preview.hasCamera ? 'on' : 'off'}
            label={preview.cameraOn ? 'Turn camera off' : 'Turn camera on'}
            disabled={!preview.hasCamera}
            onClick={() => preview.setCameraOn(!preview.cameraOn)}
          >
            {preview.cameraOn && preview.hasCamera ? <Camera /> : <CameraOff />}
          </CallButton>

          <div className="ml-auto rounded-full bg-black/45 px-3 py-1.5 backdrop-blur-sm">
            <MicMeter stream={preview.stream} muted={!preview.micOn || !preview.hasMic} />
          </div>
        </div>
      </div>

      <div
        className={cn(
          'flex flex-wrap gap-2',
          compact &&
            'max-sm:flex-nowrap max-sm:[&>div]:min-w-0 max-sm:[&>div>button]:w-full',
        )}
      >
        <DevicePicker
          icon={<Camera className="size-3.5" />}
          label="Camera"
          tone={tone}
          options={dedupeDevices(inventory.cameras)}
          selected={preview.cameraDeviceId}
          onSelect={preview.chooseCamera}
          empty="No camera"
        />
        <DevicePicker
          icon={<Mic className="size-3.5" />}
          label="Microphone"
          tone={tone}
          options={dedupeDevices(inventory.microphones)}
          selected={preview.micDeviceId}
          onSelect={preview.chooseMic}
          empty="No microphone"
        />
        {supportsOutputSelection() ? (
          <DevicePicker
            icon={<Volume2 className="size-3.5" />}
            label="Speaker"
            tone={tone}
            options={dedupeDevices(inventory.speakers)}
            selected={preview.speakerDeviceId}
            onSelect={preview.chooseSpeaker}
            empty="System default"
          />
        ) : null}
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */

/**
 * What fills the frame when there is no picture.
 *
 * Four genuinely different states, and collapsing them is how a permission
 * prompt someone dismissed becomes "loading…" forever.
 */
function PreviewPoster({ preview, displayName }: { preview: Preview; displayName: string }) {
  if (preview.status === 'requesting' || preview.status === 'idle') {
    return (
      <div className="absolute inset-0 grid place-items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-white/[0.05] to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-white/8 to-transparent motion-safe:animate-[era-sheen_1.8s_ease-in-out_infinite]" />
        <p className="relative text-sm text-white/55">Waiting for the camera…</p>
      </div>
    )
  }

  if (preview.status === 'blocked' && preview.problem) {
    return (
      <div className="absolute inset-0 grid place-items-center px-8">
        <div className="max-w-sm space-y-3 text-center">
          <AlertTriangle className="mx-auto size-6 text-[#e5b155]" strokeWidth={1.5} />
          <p className="text-sm font-medium text-white/90">{preview.problem.title}</p>
          <p className="text-[0.8125rem] leading-relaxed text-white/55">
            {preview.problem.detail}
          </p>
          {preview.problem.retryable ? (
            <button
              type="button"
              onClick={preview.retry}
              className="tap44 rounded-full border border-white/20 px-4 py-1.5 text-[0.8125rem] text-white/85 transition-colors hover:bg-white/10"
            >
              Try again
            </button>
          ) : null}
        </div>
      </div>
    )
  }

  // Camera present, deliberately off: a monogram in the person's own palette
  // colour, which is the same colour their tiles and their transcript turns
  // will carry. The avatar is not a placeholder — it is the identity.
  const color = speakerColorVar(0)
  return (
    <div className="absolute inset-0 grid place-items-center">
      <div
        className="absolute inset-0 opacity-[0.16]"
        style={{ background: `radial-gradient(60% 70% at 50% 45%, ${color}, transparent 70%)` }}
      />
      <div className="relative flex flex-col items-center gap-3">
        <div
          className="grid size-20 place-items-center rounded-full text-xl font-semibold text-white"
          style={{ backgroundColor: color }}
        >
          {initials(displayName)}
        </div>
        <p className="text-[0.8125rem] text-white/45">
          {preview.hasCamera ? 'Camera is off' : 'No camera'}
        </p>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */

function DevicePicker({
  icon,
  label,
  tone,
  options,
  selected,
  onSelect,
  empty,
}: {
  icon: React.ReactNode
  label: string
  tone: 'light' | 'dark'
  options: DeviceOption[]
  selected: string | null
  onSelect: (deviceId: string) => void
  empty: string
}) {
  const current = options.find((option) => option.deviceId === selected) ?? options[0]

  return (
    <Menu
      align="start"
      // An action sheet on a phone — see `MENU_SHEET`. There are up to three of
      // these side by side, so the second and third open a left-aligned panel
      // most of the way across the screen and the right-hand half of it lands
      // outside a window that cannot scroll.
      //
      // `tone` is doing double duty as "which surface am I on", which is what
      // it has always meant: `light` is the lobby, which has the app's tab bar
      // along the bottom of the phone, and `dark` is the prejoin door, which
      // renders outside the shell and has nothing there.
      className={cn(
        MENU_SHEET,
        tone === 'light' && 'max-sm:bottom-[calc(var(--sab)+var(--tabbar-h)+0.75rem)]',
      )}
      trigger={({ open, toggle }) => (
        <button
          type="button"
          onClick={toggle}
          disabled={options.length === 0}
          aria-label={`${label}: ${current?.label ?? empty}`}
          className={cn(
            // 44px tall on touch, and narrow enough that two of them fit on one
            // line of a 375px screen. At 15rem each they wrapped to three rows
            // — 120px of picker above a join button that then did not fit on
            // the screen at all. The label truncates; the full device name is
            // in the menu and in `aria-label`.
            'flex h-9 max-w-[9.5rem] items-center gap-2 rounded-full border px-3 pointer-coarse:h-11 sm:max-w-[15rem]',
            'text-[0.8125rem] transition-colors disabled:opacity-45',
            tone === 'dark'
              ? 'border-white/12 bg-white/[0.04] text-white/60 hover:bg-white/[0.08] hover:text-white'
              : 'border-line-strong bg-surface text-ink-muted hover:bg-surface-2 hover:text-ink',
          )}
        >
          <span className={tone === 'dark' ? 'text-white/35' : 'text-ink-dim'}>{icon}</span>
          <span className="truncate">{current?.label ?? empty}</span>
          <span className={cn('ml-auto', tone === 'dark' ? 'text-white/35' : 'text-ink-dim')}>
            <MenuChevron open={open} />
          </span>
        </button>
      )}
    >
      {(close) => (
        <>
          <MenuLabel>{label}</MenuLabel>
          {options.map((option) => (
            <MenuItem
              key={option.deviceId}
              selected={option.deviceId === current?.deviceId}
              onSelect={() => {
                onSelect(option.deviceId)
                close()
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </>
      )}
    </Menu>
  )
}

/**
 * Choosing an output device needs `HTMLMediaElement.setSinkId`, which Firefox
 * still gates behind a pref. Hiding the picker is better than offering one
 * that silently does nothing.
 */
function supportsOutputSelection(): boolean {
  return typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}
